import {
  Button,
  Container,
  PasswordInput,
  Stack,
  TextInput,
  Title,
  Anchor,
  Group,
} from "@mantine/core";
import { IconLogin2 } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { schemaResolver, useForm } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";
import { login } from "../openapi/api-client.ts";
import { z } from "zod";
import { use } from "react";
import { UserContext } from "../UserContext.ts";
import { useForwardIfLoggedIn } from "../hooks/useForwardIfLoggedIn.ts";

// todo: disable page if logged in, forward to dashboard

const schema = z.object({
  email: z.email({
    message:
      "Bitte trag deine E-Mail-Adresse ein, die du zur Registrierung genutzt hast",
  }),
  password: z.string().min(6, {
    message:
      "Bitte trag dein Passwort ein, das du bei der Registrierung angegeben hast",
  }),
});

export const Login = () => {
  useForwardIfLoggedIn("/bewerbung");

  const navigate = useNavigate();
  const userContext = use(UserContext);

  const loginMutation = useMutation({
    mutationFn: login,
  });

  const form = useForm<{ email: string; password: string }>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: schemaResolver(schema, { sync: true }),
    validateInputOnBlur: true,
    transformValues: (values) => {
      return {
        ...values,
      };
    },
  });

  const submit = form.onSubmit((values) => {
    loginMutation.mutate(
      {
        email: values.email,
        password: values.password,
      },
      {
        onSuccess: (response) => {
          if (response.response.status == 401) {
            form.setErrors({
              email: "Falsche Zugangsdaten",
              password: "Falsche Zugangsdaten",
            });
            return;
          }

          if (!response.response.ok) {
            throw new Error(response.response.statusText);
          }

          if (!response.data) {
            throw new Error("No data returned");
          }

          userContext.setUserData(response.data.userId, response.data.roles);

          if (response.data.roles.includes("candidate")) {
            void navigate("/bewerbung");
            return;
          }

          if (response.data.roles.includes("committee")) {
            void navigate("/kommission");
            return;
          }

          void navigate("/");
        },
      },
    );
  });

  return (
    <Container my="lg">
      {/*
        Ueberschrift, Kopfzeilen-Eintrag und Knopf hiessen vorher „Anmelden",
        „LOGIN" und „Login" — drei Woerter fuer dieselbe Sache auf einem
        Bildschirm. Massgeblich ist jetzt das Wort aus der Kopfzeile.
      */}
      <Title mb="lg">Login</Title>

      <form onSubmit={submit}>
        <Stack
          w={{
            md: "50%",
          }}
        >
          {/*
            Ohne `type="email"` bekommen Bewerber:innen auf dem Handy die
            falsche Tastatur, ohne `autoComplete` greifen Passwortmanager nicht.
          */}
          <TextInput
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            {...form.getInputProps("email")}
          />
          <PasswordInput
            label="Passwort"
            autoComplete="current-password"
            {...form.getInputProps("password")}
          />
          <Group gap="sm" mt="lg">
            <Button
              leftSection={<IconLogin2 />}
              color="blue"
              type="submit"
              size="lg"
              loading={loginMutation.isPending}
            >
              Login
            </Button>
            <Anchor component={Link} to="/registrieren">
              Registrieren
            </Anchor>
          </Group>
        </Stack>
      </form>
      <Anchor component={Link} to="/passwort-vergessen" mt="xl" display="block">
        Passwort vergessen?
      </Anchor>
    </Container>
  );
};

export default Login;
